import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, Loader2, Printer } from "lucide-react";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { useLang } from "../i18n";

export default function Voucher() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [v, setV] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/vouchers/${token}`).then((r) => setV(r.data)).catch(() => setError(true));
  }, [token]);

  const payKey = { online: "bon.pay_online", onboard_card: "bon.pay_card", onboard_cash: "bon.pay_cash" };

  return (
    <div className="min-h-screen bg-sand" data-testid="voucher-page">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 pt-8 print:hidden sm:px-6">
        <Link to="/" data-testid="voucher-home-link" className="text-sm font-semibold text-navy/60 hover:text-azure">
          ← CabNow
        </Link>
        {v && (
          <button
            onClick={() => window.print()}
            data-testid="voucher-print-button"
            className="flex items-center gap-2 rounded-full bg-navy px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-azure"
          >
            <Printer size={15} /> {t("bon.print")}
          </button>
        )}
      </div>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {!v && !error && (
          <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-azure" /></div>
        )}
        {error && (
          <div className="rounded-3xl bg-white p-12 text-center shadow-xl" data-testid="voucher-error">
            <h1 className="font-serif text-2xl font-semibold text-navy">{t("confirmation.notfound_title")}</h1>
            <p className="mt-3 text-sm text-navy/60">{t("confirmation.notfound_text")}</p>
          </div>
        )}

        {v && (
          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl print:rounded-none print:border-0 print:shadow-none" data-testid="voucher-card">
            <div className="flex items-center justify-between bg-navy px-8 py-8 sm:px-10">
              <div>
                <Logo light />
                <p className="mt-2 text-xs uppercase tracking-[0.3em] text-gold">{t("bon.title")}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-2xl font-bold text-gold" data-testid="voucher-number">{v.voucher_no}</p>
                <p className="mt-1 text-xs text-white/50">{t("bon.issued", { date: (v.created_at || "").slice(0, 10) })}</p>
              </div>
            </div>

            <div className="grid gap-6 border-b border-slate-100 p-8 sm:grid-cols-2 sm:p-10">
              <div>
                <p className="overline-label mb-3 !text-navy/40">{t("bon.company")}</p>
                <p className="font-serif text-lg font-semibold text-navy">CabNow</p>
                <p className="mt-1 text-sm text-navy/60">Chauffeur privé VTC — Cannes, Alpes-Maritimes</p>
                <p className="text-sm text-navy/60">+33 6 61 29 75 99 — cabnow06@gmail.com</p>
              </div>
              <div>
                <p className="overline-label mb-3 !text-navy/40">{t("bon.client")}</p>
                <p className="font-serif text-lg font-semibold text-navy" data-testid="voucher-client">{v.name}</p>
                <p className="mt-1 text-sm text-navy/60">{v.phone}</p>
                <p className="text-sm text-navy/60">{v.email}</p>
              </div>
            </div>

            <div className="p-8 sm:p-10">
              <p className="overline-label mb-5 !text-navy/40">{t("bon.ride_title")}</p>
              <dl className="space-y-3.5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-navy/50">{t("confirmation.r_pickup")}</dt>
                  <dd className="text-right font-semibold text-navy">{v.pickup_label}
                    {v.pickup_address && <span className="block text-xs font-medium text-navy/50">{v.pickup_address}</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-navy/50">{t("confirmation.r_dropoff")}</dt>
                  <dd className="text-right font-semibold text-navy">{v.dropoff_label}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-navy/50">{t("confirmation.r_date")} / {t("confirmation.r_time")}</dt>
                  <dd className="font-semibold text-navy">{v.date} — {v.time}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-navy/50">{t("confirmation.r_vehicle")}</dt>
                  <dd className="font-semibold text-navy">{v.vehicle === "berline" ? "Berline Executive" : "Van Premium"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-navy/50">{t("confirmation.r_passengers")}</dt>
                  <dd className="font-semibold text-navy">{v.passengers} · {v.luggage} {t("booking.s_luggage")}</dd>
                </div>
                {v.flight_number && (
                  <div className="flex justify-between">
                    <dt className="text-navy/50">{lang === "en" ? "Flight / train" : "Vol / train"}</dt>
                    <dd className="font-semibold text-navy">{v.flight_number}</dd>
                  </div>
                )}
                {v.child_seat && (
                  <div className="flex justify-between">
                    <dt className="text-navy/50">{lang === "en" ? "Child seat" : "Siège enfant"}</dt>
                    <dd className="font-semibold text-navy">
                      {{ "0-2": lang === "en" ? "Baby carrier (0-2 yrs)" : "Coque bébé (0-2 ans)",
                         "3-7": lang === "en" ? "Child seat (3-7 yrs)" : "Siège enfant (3-7 ans)",
                         "7+": lang === "en" ? "Booster (7+ yrs)" : "Réhausseur (7 ans et +)" }[v.child_seat_age]
                        || (lang === "en" ? "Yes" : "Oui")}
                    </dd>
                  </div>
                )}
                {v.notes && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-navy/50">Notes</dt>
                    <dd className="text-right font-medium italic text-navy/70">« {v.notes} »</dd>
                  </div>
                )}
              </dl>

              <div className="mt-8 flex items-center justify-between rounded-2xl bg-navy px-6 py-5">
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/50">{t("bon.fare")}</p>
                  <p className="mt-1 text-sm font-semibold text-white/80">{t(payKey[v.payment_method] || "bon.pay_card")}</p>
                </div>
                <p className="font-serif text-4xl font-semibold text-gold" data-testid="voucher-price">{v.price_eur} €</p>
              </div>

              <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-navy/45">
                <FileText size={13} className="mt-0.5 shrink-0" />
                {t("bon.legal")} — {t("confirmation.reference")} {v.id.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
